import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Box, Table, TableContainer, TableRow, TableBody } from '@mui/material';
import { mdiDownload } from '@mdi/js';
import { Icon } from '@mdi/react';

import { TableHead, TableCell, TableSortButton } from 'common/components/Table';
import {
    IPortfolioReportOrderField,
    IContentTypeValue,
    IFeature,
    ExportPortfolioReportDocument,
    CurrentUserDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { Typography } from 'common/components/Typography';
import { BoxLoader } from 'common/components/Loader';
import { FilterBar } from 'common/components/FilterBar';
import { Pagination } from 'common/components/Pagination';
import { useReportList } from 'report/hooks/useReportList';
import { useFeature } from 'common/hooks/useFeature';
import { Button } from 'common/components/Button';
import { ConfirmDialog } from 'common/components/ConfirmDialog';

import { PortfolioReportTableRow } from './PortfolioReportTableRow';

export const PortfolioReportTable = () => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [showExportDialog, setShowExportDialog] = useState(false);
    const { canUse: canExport } = useFeature(IFeature.Export);

    // Get current user email to use in export dialog
    const { data: currentUserData, loading: loadingCurrentUser } =
        useQuery(CurrentUserDocument);
    const currentUserEmail = currentUserData?.currentUser?.email;

    const relatedObjectTypes = [null, IContentTypeValue.ReceivedCertificate];

    const {
        items,
        loading: loadingReport,
        count,
        onSearch,
        onFilterChange,
        filters,
        filterValues,
        onFilterChangeValue,
        currentSort,
        onSort,
        setPage,
        paginationSettings,
        filterValuesForQuery,
    } = useReportList('portfolio', false, {
        relatedObjectTypes,
        withAggregatedFilterValues: true,
        isCertificateOrModuleReport: true,
    });

    const loading = loadingReport || loadingCurrentUser;

    const [exportReport, { loading: loadingExport }] = useMutation(
        ExportPortfolioReportDocument,
        {
            variables: {
                relatedObjectTypes,
                ...filterValuesForQuery,
            },
        }
    );

    const handleExport = async () => {
        try {
            const exportResults = await exportReport();
            const fileUrl = exportResults.data?.exportPortfolioReport?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(translate('reportPage.exportSuccess'), {
                    variant: 'success',
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    return (
        <>
            <FilterBar
                useFilterDrawer
                barAppend={
                    canExport && (
                        <Button
                            disabled={count === 0}
                            startIcon={
                                <Icon path={mdiDownload} size="1.9rem" />
                            }
                            sx={{ flex: 1 }}
                            variant="outlined"
                            onClick={() => setShowExportDialog(true)}
                        />
                    )
                }
                filterDrawerProps={{
                    title: translate('reportPage.filterFormPortfolio.title'),
                    description: translate(
                        'reportPage.filterFormPortfolio.description'
                    ),
                    formProps: {
                        onReset: (values) => {
                            setPage(1);
                            onFilterChange(values);
                        },
                    },
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onChangeField={onFilterChangeValue}
                onFiltersChange={(values) => {
                    setPage(1);
                    onFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !!items.length && (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {translate('user')}
                                    <TableSortButton
                                        currentSortDirection={
                                            currentSort.direction
                                        }
                                        currentSortField={
                                            currentSort.field as string
                                        }
                                        sortField={
                                            IPortfolioReportOrderField.UserName
                                        }
                                        onClick={() =>
                                            onSort(
                                                IPortfolioReportOrderField.UserName
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {translate('item')}
                                    <TableSortButton
                                        currentSortDirection={
                                            currentSort.direction
                                        }
                                        currentSortField={
                                            currentSort.field as string
                                        }
                                        sortField={
                                            IPortfolioReportOrderField.Title
                                        }
                                        onClick={() =>
                                            onSort(
                                                IPortfolioReportOrderField.Title
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {translate('portfolioStartDate')}
                                    <TableSortButton
                                        currentSortDirection={
                                            currentSort.direction
                                        }
                                        currentSortField={
                                            currentSort.field as string
                                        }
                                        sortField={
                                            IPortfolioReportOrderField.StartDate
                                        }
                                        onClick={() =>
                                            onSort(
                                                IPortfolioReportOrderField.StartDate
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {translate('portfolioEndDate')}
                                    <TableSortButton
                                        currentSortDirection={
                                            currentSort.direction
                                        }
                                        currentSortField={
                                            currentSort.field as string
                                        }
                                        sortField={
                                            IPortfolioReportOrderField.EndDate
                                        }
                                        onClick={() =>
                                            onSort(
                                                IPortfolioReportOrderField.EndDate
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    {translate('portfolioPoints')}
                                </TableCell>
                                <TableCell>{translate('status')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items?.map((reportItem) => (
                                <PortfolioReportTableRow
                                    key={reportItem.id}
                                    reportItem={reportItem}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loading && <BoxLoader />}

            {!loading && !items.length && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography>
                        {translate('noOptionsText.reportItems')}
                    </Typography>
                </Box>
            )}

            {!loading && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate('portfolioReportItems', {
                            count,
                        })}
                        onChange={(page: number) => {
                            setPage(page);
                        }}
                    />
                </Box>
            )}

            <ConfirmDialog
                confirmText={translate('export')}
                loading={loadingExport}
                open={showExportDialog}
                title={translate('reportPage.exportPortfolioDialog.title')}
                onCancel={() => setShowExportDialog(false)}
                onClose={() => setShowExportDialog(false)}
                onConfirm={handleExport}
            >
                {translate('reportPage.exportPortfolioDialog.text', {
                    email: currentUserEmail,
                })}
            </ConfirmDialog>
        </>
    );
};
